import { useMemo, useState } from 'react';
import { ManufacturingEquipment } from '../../types/manufacturingEquipment';

export type EquipListSortKey = 'sortOrder' | 'equipmentCode';
export type EquipListSortDir = 'asc' | 'desc';

export function compareEquipList(
  a: ManufacturingEquipment,
  b: ManufacturingEquipment,
  sortKey: EquipListSortKey,
  sortDir: EquipListSortDir
): number {
  const dir = sortDir === 'asc' ? 1 : -1;
  if (sortKey === 'sortOrder') {
    const sa = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
    const sb = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
    if (sa !== sb) return (sa - sb) * dir;
    return (a.equipmentId - b.equipmentId) * dir;
  }
  const ca = (a.equipmentCode || '').trim();
  const cb = (b.equipmentCode || '').trim();
  if (!ca && !cb) return (a.equipmentId - b.equipmentId) * dir;
  if (!ca) return 1;
  if (!cb) return -1;
  const cmp = ca.localeCompare(cb, 'ko', { numeric: true, sensitivity: 'base' });
  if (cmp !== 0) return cmp * dir;
  return (a.equipmentId - b.equipmentId) * dir;
}

export function useEquipListSort(
  initialKey: EquipListSortKey = 'equipmentCode',
  initialDir: EquipListSortDir = 'desc'
) {
  const [sortKey, setSortKey] = useState<EquipListSortKey>(initialKey);
  const [sortDir, setSortDir] = useState<EquipListSortDir>(initialDir);

  const toggleSort = (key: EquipListSortKey) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir(key === 'equipmentCode' ? 'desc' : 'asc');
  };

  const sortMark = (key: EquipListSortKey) =>
    sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  const thClass = (key: EquipListSortKey) =>
    `is-sortable${sortKey === key ? ' is-active' : ''}`;

  return { sortKey, sortDir, toggleSort, sortMark, thClass };
}

export function useSortedEquipList(
  items: ManufacturingEquipment[],
  sortKey: EquipListSortKey,
  sortDir: EquipListSortDir
) {
  return useMemo(
    () => items.slice().sort((a, b) => compareEquipList(a, b, sortKey, sortDir)),
    [items, sortKey, sortDir]
  );
}
